import { animate, splitText, stagger } from 'animejs';

export default class SplitTextAnimator {
    /**
     * @param {string|Element} target - CSS selector or element to animate
     * @param {Object} [options]
     * @param {number} [options.staggerMs=25]
     * @param {number} [options.charDuration=400]
     * @param {string} [options.charEasing='easeOutExpo']
     * @param {string} [options.underlineEasing='easeOutQuad']
     * @param {number} [options.translateY=20]
     * @param {boolean} [options.autoInit=true]
     */
    constructor(target, options = {}) {
        this.elements =
            typeof target === 'string'
                ? Array.from(document.querySelectorAll(target))
                : [target];

        this.options = {
            staggerMs: 60,
            charDuration: 800,
            charEasing: 'easeOutExpo',
            underlineEasing: 'easeOutQuad',
            translateY: 10,
            autoInit: true,
            ...options,
        };

        this.splitResults = [];
        this.animations = [];

        if (this.options.autoInit) {
            this.init();
        }
    }

    init() {
        this.elements.forEach((el) => this._animateElement(el));
        return this;
    }

    _animateElement(el) {
        const { staggerMs, charDuration, charEasing, translateY, underlineEasing } =
            this.options;

        const split = splitText(el, { words: true, chars: true });
        this.splitResults.push(split);

        // Reveal the container now the split markup is in place.
        // Chars/underlines stay invisible (their own styles handle that)
        // until the animations below actually run.
        el.style.opacity = '1';

        const charAnim = animate(split.chars, {
            opacity: [0, 1],
            translateY: [translateY, 0],
            easing: charEasing,
            duration: charDuration,
            delay: stagger(staggerMs),
        });
        this.animations.push(charAnim);

        // Group each char by its nearest <u> ancestor (if any) — no class-name dependency
        const underlineGroups = new Map(); // u element -> array of char indices

        split.chars.forEach((charEl, index) => {
            const u = charEl.closest('u');
            if (!u) return;
            if (!underlineGroups.has(u)) underlineGroups.set(u, []);
            underlineGroups.get(u).push(index);
        });

        underlineGroups.forEach((indices, u) => {
            const firstIndex = indices[0];
            const lastIndex = indices[indices.length - 1];
            const midIndex = indices[Math.floor(indices.length / 2)];

            const startDelay = midIndex * staggerMs;
            const endOfChars = lastIndex * staggerMs + charDuration;

            const underlineAnim = animate(u, {
                backgroundSize: ['0% 2px', '100% 2px'],
                easing: underlineEasing,
                duration: endOfChars - startDelay,
                delay: startDelay,
            });
            this.animations.push(underlineAnim);
        });
    }

    restart() {
        this.animations.forEach((anim) => anim.restart());
    }

    pause() {
        this.animations.forEach((anim) => anim.pause());
    }

    play() {
        this.animations.forEach((anim) => anim.play());
    }

    /** Revert DOM splitting and stop animations */
    destroy() {
        this.animations.forEach((anim) => anim.pause());
        this.animations = [];
        this.splitResults.forEach((split) => split.revert && split.revert());
        this.splitResults = [];
    }
}
